import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { EnvelopeSimple, Phone, MapPin, Clock } from '@phosphor-icons/react';
import { submitLeadWithGeoLocation, getCountryInfo } from '../services/centralLeadApi';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [countryCode, setCountryCode] = useState('+1');
  const [detectedCountry, setDetectedCountry] = useState(null);
  const [errors, setErrors] = useState({ email: '', phone: '' });
  const [isLoading, setIsLoading] = useState(false);

  const countryCodes = [
    { code: '+1', country: 'US', flag: '\u{1F1FA}\u{1F1F8}', name: 'United States' },
    { code: '+1', country: 'CA', flag: '\u{1F1E8}\u{1F1E6}', name: 'Canada' },
    { code: '+44', country: 'GB', flag: '\u{1F1EC}\u{1F1E7}', name: 'United Kingdom' },
    { code: '+91', country: 'IN', flag: '\u{1F1EE}\u{1F1F3}', name: 'India' },
    { code: '+92', country: 'PK', flag: '\u{1F1F5}\u{1F1F0}', name: 'Pakistan' },
    { code: '+61', country: 'AU', flag: '\u{1F1E6}\u{1F1FA}', name: 'Australia' },
    { code: '+49', country: 'DE', flag: '\u{1F1E9}\u{1F1EA}', name: 'Germany' },
    { code: '+33', country: 'FR', flag: '\u{1F1EB}\u{1F1F7}', name: 'France' },
    { code: '+81', country: 'JP', flag: '\u{1F1EF}\u{1F1F5}', name: 'Japan' },
    { code: '+86', country: 'CN', flag: '\u{1F1E8}\u{1F1F3}', name: 'China' },
    { code: '+55', country: 'BR', flag: '\u{1F1E7}\u{1F1F7}', name: 'Brazil' },
    { code: '+971', country: 'AE', flag: '\u{1F1E6}\u{1F1EA}', name: 'UAE' },
    { code: '+966', country: 'SA', flag: '\u{1F1F8}\u{1F1E6}', name: 'Saudi Arabia' },
    { code: '+65', country: 'SG', flag: '\u{1F1F8}\u{1F1EC}', name: 'Singapore' },
  ];

  useEffect(() => {
    (async () => {
      try {
        const info = await getCountryInfo();
        if (info) {
          setDetectedCountry(info);
          const match = countryCodes.find(c => c.country === info.countryCode);
          if (match) setCountryCode(match.code);
        }
      } catch (e) { console.error('Geo fetch error:', e); }
    })();
  }, []);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePhone = (phone) => phone.replace(/[^\d]/g, '').length >= 10;

  const handleSubmit = async (e) => {
    e.preventDefault();
    let hasErrors = false;
    const newErrors = { email: '', phone: '' };
    if (!validateEmail(formData.email)) { newErrors.email = 'Please enter a valid email'; hasErrors = true; }
    if (!validatePhone(formData.phone)) { newErrors.phone = 'Please enter a valid phone number'; hasErrors = true; }
    if (hasErrors) { setErrors(newErrors); toast.error('Please fix errors'); return; }

    setIsLoading(true);
    try {
      const leadData = {
        name: formData.name, email: formData.email, phone: formData.phone,
        countryCode, country: countryCodes.find(c => c.code === countryCode)?.country || 'US',
        description: formData.message
      };
      const result = await submitLeadWithGeoLocation(leadData, null, 'Contact Us Page');
      if (result.success) {
        toast.success(result.message || 'Message sent successfully!');
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        toast.error(result.message || 'Failed to send. Please try again.');
        setIsLoading(false);
      }
    } catch (error) {
      console.error('Submission error:', error);
      toast.error('An error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-[72px]">
      <section data-testid="contact-hero" className="bg-slate-900 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs sm:text-sm uppercase tracking-[0.2em] font-bold text-[#D4AF37] mb-4">Get In Touch</p>
          <h1 className="text-4xl sm:text-5xl font-heading font-semibold text-white tracking-tighter mb-4">Contact Us</h1>
          <p className="text-base text-slate-400 max-w-2xl mx-auto">
            Questions about protecting your intellectual property? Our team is ready to help.
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            <div className="space-y-5">
              {[
                { icon: Phone, title: 'Phone', line1: '+1 (877) 954 2409', line2: 'Mon-Fri, 9AM-6PM EST', href: 'tel:+18779542409' },
                { icon: EnvelopeSimple, title: 'Email', line1: 'info@trademarkthrone.com', line2: 'Response within 24 hours', href: 'mailto:info@trademarkthrone.com' },
                { icon: MapPin, title: 'Office', line1: '765 Market St APT 34D', line2: 'San Francisco, CA 94103' },
                { icon: Clock, title: 'Hours', line1: 'Monday - Friday: 9AM - 6PM', line2: 'Saturday: 10AM - 4PM' },
              ].map((item, i) => (
                <Card key={i} data-testid={`contact-info-${i}`} className="p-6 border border-slate-200 rounded-md">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#044B3B] rounded-md flex items-center justify-center flex-shrink-0">
                      <item.icon className="h-5 w-5 text-white" weight="regular" />
                    </div>
                    <div>
                      <h3 className="text-sm font-heading font-bold text-slate-900 mb-1">{item.title}</h3>
                      {item.href ? (
                        <p className="text-sm text-slate-600"><a href={item.href} className="text-slate-600 no-underline hover:no-underline">{item.line1}</a></p>
                      ) : (
                        <p className="text-sm text-slate-600">{item.line1}</p>
                      )}
                      <p className="text-xs text-slate-400 mt-0.5">{item.line2}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            <div className="lg:col-span-2">
              <Card data-testid="contact-form-card" className="p-8 border border-slate-200 rounded-md">
                <h2 className="text-2xl font-heading font-bold text-slate-900 mb-6">Send Us a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input data-testid="contact-name-input" type="text" placeholder="Full Name *" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required className="border-slate-200 focus:border-[#044B3B] rounded-md" />
                    <div>
                      <Input data-testid="contact-email-input" type="email" placeholder="Email Address *" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})}
                        onBlur={() => { if (formData.email && !validateEmail(formData.email)) setErrors(prev => ({...prev, email: 'Enter a valid email'})); else setErrors(prev => ({...prev, email: ''})); }}
                        className={`${errors.email ? 'border-red-500' : 'border-slate-200'} focus:border-[#044B3B] rounded-md`} required />
                      {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                      Phone <span className="text-red-500">*</span>
                      {detectedCountry && <span className="ml-2 text-xs text-slate-400 font-normal">(Detected: {detectedCountry.countryName})</span>}
                    </label>
                    <div className="flex gap-2">
                      <Select value={countryCode} onValueChange={setCountryCode}>
                        <SelectTrigger data-testid="contact-country-select" className="w-28 px-2 border border-slate-200 rounded-md bg-white text-sm font-medium text-slate-700 focus:border-[#044B3B]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-[200px]">
                          {countryCodes.map((c, i) => (<SelectItem key={i} value={c.code}>{c.flag} {c.code}</SelectItem>))}
                        </SelectContent>
                      </Select>
                      <Input data-testid="contact-phone-input" type="tel" placeholder="123-456-7890" value={formData.phone}
                        onChange={(e) => { const v = e.target.value.replace(/[^\d\s\-]/g, ''); setFormData({...formData, phone: v}); }}
                        onBlur={() => { if (formData.phone && !validatePhone(formData.phone)) setErrors(prev => ({...prev, phone: 'Enter 10+ digits'})); else setErrors(prev => ({...prev, phone: ''})); }}
                        className={`flex-1 ${errors.phone ? 'border-red-500' : 'border-slate-200'} focus:border-[#044B3B] rounded-md`} required />
                    </div>
                    {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                  </div>
                  <Textarea data-testid="contact-message-input" placeholder="What are you looking to protect? (e.g. Name, Logo and Slogan)" value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} required rows={5}
                    className="border-slate-200 focus:border-[#044B3B] rounded-md" />
                  <Button data-testid="contact-submit-btn" type="submit" disabled={isLoading}
                    className="w-full bg-[#044B3B] hover:bg-[#03392D] text-white font-semibold text-sm py-5 rounded-md">
                    {isLoading ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
